import { useMemo } from 'react'

export interface LTVInput {
  grayBookRetailPrice: number
  salesPrice: number
  salesTaxPercent: number
  dmvFees: number
  docFee: number
  downPayment: number
  tradeInValue: number
  tradeInTaxCredit: boolean
  interestRate: number
  loanTermMonths: number
}

export type LTVRiskLevel = 'low' | 'moderate' | 'high' | 'critical'

export interface LTVOutput {
  taxableAmount: number
  salesTaxAmount: number
  totalFees: number
  loanAmount: number
  /** @deprecated Use financedLtvPercent instead */
  ltvPercent: number
  /** @deprecated Use financedRiskLevel instead */
  riskLevel: LTVRiskLevel
  vehicleLtvPercent: number
  vehicleRiskLevel: LTVRiskLevel
  financedLtvPercent: number
  financedRiskLevel: LTVRiskLevel
  monthlyPayment: number
  totalLoanCost: number
  totalInterest: number
}

export function getRiskLevel(ltvPercent: number): LTVRiskLevel {
  if (ltvPercent <= 80) return 'low'
  if (ltvPercent <= 100) return 'moderate'
  if (ltvPercent <= 120) return 'high'
  return 'critical'
}

function calculateMonthlyPayment(principal: number, annualRate: number, months: number): number {
  if (months <= 0) return 0
  if (annualRate === 0) return principal / months
  const r = annualRate / 100 / 12
  const n = months
  return principal * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)
}

export function computeLTV(input: LTVInput): LTVOutput {
  const {
    grayBookRetailPrice,
    salesPrice,
    salesTaxPercent,
    dmvFees,
    docFee,
    downPayment,
    tradeInValue,
    tradeInTaxCredit,
    interestRate,
    loanTermMonths,
  } = input

  const taxableAmount = tradeInTaxCredit
    ? Math.max(0, salesPrice - tradeInValue)
    : salesPrice

  const salesTaxAmount = taxableAmount * (salesTaxPercent / 100)
  const totalFees = dmvFees + docFee
  const loanAmount = salesPrice + salesTaxAmount + totalFees - downPayment - tradeInValue

  // Financed LTV: total loan (incl. tax/fees) vs. graybook
  const financedLtvPercent = grayBookRetailPrice > 0 ? (loanAmount / grayBookRetailPrice) * 100 : 0
  const financedRiskLevel = getRiskLevel(financedLtvPercent)

  // Vehicle LTV: vehicle price net of down/trade vs. graybook (excludes tax/fees)
  const vehicleLoanAmount = salesPrice - downPayment - tradeInValue
  const vehicleLtvPercent = grayBookRetailPrice > 0 ? (vehicleLoanAmount / grayBookRetailPrice) * 100 : 0
  const vehicleRiskLevel = getRiskLevel(vehicleLtvPercent)

  const effectiveLoan = Math.max(0, loanAmount)
  const monthlyPayment = calculateMonthlyPayment(effectiveLoan, interestRate, loanTermMonths)
  const totalLoanCost = monthlyPayment * loanTermMonths
  const totalInterest = totalLoanCost - effectiveLoan

  return {
    taxableAmount,
    salesTaxAmount: Math.round(salesTaxAmount * 100) / 100,
    totalFees,
    loanAmount: Math.round(loanAmount * 100) / 100,
    ltvPercent: Math.round(financedLtvPercent * 100) / 100,
    riskLevel: financedRiskLevel,
    vehicleLtvPercent: Math.round(vehicleLtvPercent * 100) / 100,
    vehicleRiskLevel,
    financedLtvPercent: Math.round(financedLtvPercent * 100) / 100,
    financedRiskLevel,
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    totalLoanCost: Math.round(totalLoanCost * 100) / 100,
    totalInterest: Math.round(totalInterest * 100) / 100,
  }
}

export function useLTVCalculation(input: LTVInput | null): LTVOutput | null {
  return useMemo(() => {
    if (!input || input.grayBookRetailPrice <= 0 || input.salesPrice <= 0) return null
    return computeLTV(input)
  }, [
    input?.grayBookRetailPrice,
    input?.salesPrice,
    input?.salesTaxPercent,
    input?.dmvFees,
    input?.docFee,
    input?.downPayment,
    input?.tradeInValue,
    input?.tradeInTaxCredit,
    input?.interestRate,
    input?.loanTermMonths,
  ])
}

// --- Target LTV (Reverse Calculator) ---

export interface TargetLTVInput {
  targetLtvPercent: number
  grayBookRetailPrice: number
  salesPrice: number
  salesTaxPercent: number
  dmvFees: number
  docFee: number
  tradeInValue: number
  tradeInTaxCredit: boolean
  interestRate: number
  loanTermMonths: number
}

export interface TargetLTVOutput {
  minDownPayment: number
  minDownPaymentMonthly: number
  minDownPaymentLtv: number
  maxSalePrice: number
  maxSalePriceMonthly: number
  maxSalePriceLtv: number
}

const round2 = (n: number) => Math.round(n * 100) / 100

export function computeTargetLTV(input: TargetLTVInput): TargetLTVOutput {
  const {
    targetLtvPercent, grayBookRetailPrice, salesPrice, salesTaxPercent,
    dmvFees, docFee, tradeInValue, tradeInTaxCredit, interestRate, loanTermMonths,
  } = input

  const taxRate = salesTaxPercent / 100
  const totalFees = dmvFees + docFee
  const maxFinancedAmount = (targetLtvPercent / 100) * grayBookRetailPrice

  // Scenario 1: Min down payment at current sale price to hit target LTV
  const taxable1 = tradeInTaxCredit ? Math.max(0, salesPrice - tradeInValue) : salesPrice
  const salesTax1 = taxable1 * taxRate
  const totalBeforeDown = salesPrice + salesTax1 + totalFees - tradeInValue
  const minDownPayment = Math.max(0, totalBeforeDown - maxFinancedAmount)
  const scenario1Loan = Math.max(0, totalBeforeDown - minDownPayment)
  const scenario1Monthly = calculateMonthlyPayment(scenario1Loan, interestRate, loanTermMonths)
  const scenario1Ltv = grayBookRetailPrice > 0 ? (scenario1Loan / grayBookRetailPrice) * 100 : 0

  // Scenario 2: Max sale price with $0 down to hit target LTV
  // Solve: salePrice + tax(salePrice) + fees - tradeIn = maxFinancedAmount
  // With trade-in tax credit: tax = (salePrice - tradeIn) * taxRate
  //   salePrice + (salePrice - tradeIn) * taxRate + fees - tradeIn = maxFinancedAmount
  //   salePrice * (1 + taxRate) = maxFinancedAmount - fees + tradeIn + tradeIn * taxRate
  //   salePrice = (maxFinancedAmount - fees + tradeIn * (1 + taxRate)) / (1 + taxRate)
  // Without: tax = salePrice * taxRate
  //   salePrice * (1 + taxRate) + fees - tradeIn = maxFinancedAmount
  //   salePrice = (maxFinancedAmount - fees + tradeIn) / (1 + taxRate)
  const maxSalePrice = tradeInTaxCredit
    ? Math.max(0, (maxFinancedAmount - totalFees + tradeInValue * (1 + taxRate)) / (1 + taxRate))
    : Math.max(0, (maxFinancedAmount - totalFees + tradeInValue) / (1 + taxRate))

  const taxable2 = tradeInTaxCredit ? Math.max(0, maxSalePrice - tradeInValue) : maxSalePrice
  const salesTax2 = taxable2 * taxRate
  const scenario2Loan = Math.max(0, maxSalePrice + salesTax2 + totalFees - tradeInValue)
  const scenario2Monthly = calculateMonthlyPayment(scenario2Loan, interestRate, loanTermMonths)
  const scenario2Ltv = grayBookRetailPrice > 0 ? (scenario2Loan / grayBookRetailPrice) * 100 : 0

  return {
    minDownPayment: round2(minDownPayment),
    minDownPaymentMonthly: round2(scenario1Monthly),
    minDownPaymentLtv: round2(scenario1Ltv),
    maxSalePrice: round2(maxSalePrice),
    maxSalePriceMonthly: round2(scenario2Monthly),
    maxSalePriceLtv: round2(scenario2Ltv),
  }
}

export function useTargetLTVCalculation(input: TargetLTVInput | null): TargetLTVOutput | null {
  return useMemo(() => {
    if (!input || input.grayBookRetailPrice <= 0 || input.targetLtvPercent <= 0) return null
    return computeTargetLTV(input)
  }, [
    input?.targetLtvPercent,
    input?.grayBookRetailPrice,
    input?.salesPrice,
    input?.salesTaxPercent,
    input?.dmvFees,
    input?.docFee,
    input?.tradeInValue,
    input?.tradeInTaxCredit,
    input?.interestRate,
    input?.loanTermMonths,
  ])
}
